<script setup lang="ts">
import { getProductRoute } from "@shopware-pwa/helpers-next";

import { onClickOutside, useFocus, useMagicKeys } from "@vueuse/core";
import { useTemplateRef } from "vue";

const emit = defineEmits<{
  (e: "link-clicked"): void;
}>();

const { searchTerm, search, getProducts, getTotal, loading } =
  useProductSearchSuggest();

// True when the search bar is active and user can type in the search field
const active = ref(false);

// Reference to the search container
const searchContainer = useTemplateRef("searchContainer");
const searchInput = useTemplateRef("searchInput");
const localePath = useLocalePath();
const { formatLink } = useInternationalization(localePath);
const route = useRoute();

const props = withDefaults(
  defineProps<{
    displayTotal?: number;
    mobile?: boolean;
  }>(),
  { displayTotal: 10, mobile: false }
);

watch(() => props.mobile, (isMobile) => {
  if (isMobile) {
    active.value = true;
    nextTick(() => {
      searchInput.value?.focus();
    });
  }
}, { immediate: true });

watch(active, (value) => {
  const { focused } = useFocus(searchInput);

  focused.value = value;
});

// String the user has typed in the search field
const typingQuery = ref("");

watch(typingQuery, (value) => {
  if (value.length >= 3) {
    performSuggestSearch(value);
  }
});

watch(route, () => {
  if (route.query.search) {
    return;
  }
  typingQuery.value = "";
  active.value = false;
});

// Defer the search request to prevent the search from being triggered too after typing
const performSuggestSearch = useDebounceFn((value) => {
  searchTerm.value = value;
  search();
}, 300);

// Suggest results will only be shown, when the search bar is active and the user has typed more than three characters in the search field
const showSuggest = computed(() => {
  return typingQuery.value.length >= 3 && active.value;
});

if (import.meta.client) {
  onClickOutside(searchContainer, () => {
    active.value = false;
  });
}

// @ts-expect-error - wait for vueuse to adapt to templateRefs
const { enter } = useMagicKeys({ target: searchInput });
const { push } = useRouter();

const sideMenuModal = useSideMenuModal();

watch(enter, (value) => {
  if (!value) return;

  sideMenuModal.close();

  active.value = false;
  emit("link-clicked");
  push(formatLink("/search?search=" + typingQuery.value));
});
</script>

<template>
  <div
    ref="searchContainer"
    class="relative block"
    :class="[active ? 'active' : 'inactive']"
  >
    <div class="flex items-center">
      <input
        ref="searchInput"
        v-model="typingQuery"
        name="search"
        data-testid="layout-search-input"
        type="text"
        aria-label="Search for products"
        class="w-full md:w-96 h-12 bg-light-grey rounded-full px-4 py-3 pl-12 pr-4 text-base font-normal focus:outline-none text-black placeholder-medium-grey"
        :placeholder="$t('form.searchPlaceholder')"
        @click="active = true"
      />
      <button
        type="submit"
        class="absolute top-1/2 left-3 transform -translate-y-1/2 w-6 h-6 text-medium-grey bg-transparent border-none"
      >
        <img src="assets/images/search-icon.svg" alt="Search" class="w-6 h-6" />
      </button>
    </div>
    <div
      v-if="showSuggest"
      data-testid="layout-search-result-box"
      class="absolute border-secondary-100 border-1 duration-300 left-0 mt-2 bg-white overflow-hidden right-0 rounded-xl shadow-lg transition-height w-auto z-1"
    >
      <NuxtLink
        v-for="product in getProducts?.slice(0, displayTotal)"
        :key="product.id"
        :to="formatLink(getProductRoute(product))"
        data-testid="layout-search-suggest-link"
        @click="[(active = false), $emit('link-clicked')]"
      >
        <ProductSuggestSearch :product="product" />
      </NuxtLink>

      <div
        class="h-11 text-sm rounded-b-md p-3 text-center transition"
        style="clip-path: inset(0% 0% 0% 0%)"
        :class="[loading ? ['bg-primary'] : ['bg-secondary-100']]"
      >
        <div v-if="loading" class="w-full h-5 flex items-center justify-center">
          <div class="relative w-5 h-5">
            <div
              class="absolute top-0 left-0 w-5 h-5 border-1 border-extra-light-grey rounded-full"
            ></div>
            <div
              class="absolute top-0 left-0 w-5 h-5 border-1 border-red-bg rounded-full animate-spin"
              style="border-top-color: transparent; animation-duration: 0.5s"
            ></div>
          </div>
        </div>
        <div v-else>
          <NuxtLink
            v-if="getTotal > 0"
            data-testid="layout-search-result-box-more-link"
            :to="
              formatLink({ path: `/search`, query: { search: typingQuery } })
            "
            @click="[(active = false), $emit('link-clicked')]"
          >
            {{ $t("search.see") }}
            <span v-if="getTotal !== 1">{{ $t("search.all") }}</span>
            {{ getTotal }}
            <span>{{ $t("search.result", getTotal) }}</span>
          </NuxtLink>
          <div v-else data-testid="layout-search-result-box-no-result">
            {{ $t("search.noResults") }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
